'use client';
import {
  Box,
  Button,
  Divider,
  Grid,
  IconButton,
  List,
  ListItem,
  Stack,
  Theme,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import Link from 'next/link';
import { useSelectedLayoutSegments } from 'next/navigation';
import { Logo } from '../../assets/logo';
import { COLORS, fadeInUpSx, MOTION, RADIUS, REDUCED_MOTION_NO_TRANSITION, SHADOWS } from '../../constants';
import { useMenuLayout } from '../../layouts/menuLayout/context';
import { MaterialIcon } from '../materialIcon';
import { MaterialIconType } from '../materialIcon/types';
import { MenuButton, MenuButtonModel } from '../menuButton';
import { Transition } from '../transition';

export type MenuModel = {
  buttonList: MenuButtonModel[];
  headerTitle?: string;
  // 'caption' shrinks the title to a grey line under the logo, so the actions lead
  headerTitleVariant?: 'title' | 'caption';
  headerContent?: React.JSX.Element;
  headerButtons?: {
    action: any;
    text: string;
    icon?: MaterialIconType;
    variant?: 'contained' | 'outlined';
    // Route segment the button leads to, marks it as current
    title?: string;
    // Leaves the app: shows a trailing arrow so it doesn't come as a surprise
    external?: boolean;
  }[];
  // Who is signed in, shown as a card with the logout action
  account?: { name: string; details?: string[]; initials?: string; icon?: MaterialIconType };
  userData?: string[];
  closeSession?: () => void;
  closeMenu?: () => void;
  showContactData?: boolean;
  collapsedMenu?: boolean;
  setCollapsedMenu?: (_: boolean) => void;
};

const bodyFont = (theme: Theme) => theme.typography.body2.fontFamily;

const sectionLabelSx = {
  px: 2,
  pb: 0.5,
  fontSize: '0.6875rem',
  fontWeight: 600,
  letterSpacing: '0.05em',
  textTransform: 'uppercase',
  color: COLORS.text.placeholder,
} as const;

/** Up to two initials of a name, for avatar circles */
export const getInitials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0])
    .join('')
    .toUpperCase();

/**
 * Renders the commerce navigation menu component.
 * @param {MenuModel} props - The component properties.
 *
 * @return {React.JSX.Element} The rendered Menu component.
 */

export const Menu = ({
  buttonList,
  headerTitle,
  headerTitleVariant = 'title',
  headerContent,
  headerButtons,
  account,
  userData,
  closeSession,
  closeMenu: closeMenuProp,
  showContactData = false,
  collapsedMenu: collapsedMenuProp,
  setCollapsedMenu: setCollapsedMenuProp,
}: MenuModel) => {
  // Props win; inside MenuLayout the drawer state comes from context
  const menuLayout = useMenuLayout();
  const closeMenu = closeMenuProp ?? menuLayout.closeMenu;
  const collapsedMenu = collapsedMenuProp ?? menuLayout.collapsedMenu;
  const setCollapsedMenu = setCollapsedMenuProp ?? menuLayout.setCollapsedMenu;
  // Filters Next.js sections to access real segments
  const segment = useSelectedLayoutSegments().filter((str) => !/\(.*?\)/.test(str));
  const isDesktop = useMediaQuery('(min-width: 1200px)', { noSsr: true });
  const isShort = useMediaQuery('(max-height: 700px)', { noSsr: true });
  const desktopcollapsedMenu = isDesktop && collapsedMenu;
  const isCaptionTitle = headerTitleVariant === 'caption';
  const titleGap = isCaptionTitle ? 0.5 : isShort ? 1 : 2;
  // Logo pinned at the same height in every app: 32px on desktop, in line with the page content frame, and 24px in
  // the mobile drawer, where the close button floats beside it. The block fits its content (logo, gap, title
  // line, 12px below) so the next item sits at the same distance whether there is a title or not
  const logoTop = isDesktop ? 32 : 24;
  const headerHeight = desktopcollapsedMenu
    ? logoTop + 38 + 12
    : headerTitle
      ? logoTop + 25 + titleGap * 8 + (isCaptionTitle ? 18 : 20) + 12
      : logoTop + 25 + 12;
  const hasAccountCard = Boolean(account || userData?.length);

  const accountLines = account ? [account.name, ...(account.details ?? [])] : (userData ?? []);

  const logoutButton = (
    <Tooltip title={desktopcollapsedMenu ? 'Cerrar sesión' : ''} placement="right" arrow>
      <MenuButton
        icon="logout"
        iconSize={hasAccountCard ? 20 : undefined}
        text={desktopcollapsedMenu ? '' : 'Cerrar sesión'}
        aria-label={desktopcollapsedMenu ? 'Cerrar sesión' : undefined}
        onClick={closeSession}
        sx={{
          color: COLORS.state.error,
          '& .MuiIcon-root': { color: 'inherit' },
          ...(hasAccountCard && { minHeight: 40, px: 0.5, fontFamily: bodyFont, fontWeight: 500 }),
          ...(desktopcollapsedMenu && { justifyContent: 'center', px: 0 }),
        }}
      />
    </Tooltip>
  );

  return (
    // The collapse toggle straddles the drawer edge, so it lives outside the scrolling box: inside it, the overhang
    // made the menu scroll sideways (a visible scrollbar on Windows)
    <Box sx={{ position: 'relative', height: '100%' }}>
      <Stack direction={'row'} sx={{ position: 'relative', height: '100%', overflowY: 'auto', overflowX: 'hidden' }}>
        {closeMenu && (
          // Floats over the header, centered on the logo line, instead of taking a row of its own above it
          <IconButton
            aria-label="Cerrar menú"
            data-testid="menu-close"
            onClick={closeMenu}
            sx={{
              display: { xs: 'inline-flex', lg: 'none' },
              position: 'absolute',
              top: logoTop + 25 / 2 - 20,
              right: 8,
              zIndex: 1,
              width: 40,
              height: 40,
              // Plain muted icon at rest: a filled circle reads heavier than the menu items. The tint shows only on a
              // real hover, so it does not stay behind after a tap on touch screens
              color: COLORS.text.light,
              backgroundColor: 'transparent',
              '&:hover': { backgroundColor: 'transparent' },
              '@media (hover: hover)': { '&:hover': { backgroundColor: COLORS.background[0] } },
              '&:active': { transform: `scale(${MOTION.pressScale})` },
              '&.Mui-focusVisible': { outline: `2px solid ${COLORS.brand.main}`, outlineOffset: 2 },
            }}
          >
            <MaterialIcon icon="close" sx={{ color: 'inherit' }} />
          </IconButton>
        )}
        <Stack data-testid="menu" sx={{ height: '100%', width: '100%', px: 2, py: 0 }} spacing={isShort ? 2 : 3}>
          <Grid
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'flex-start',
              alignItems: 'center',
              flexDirection: 'column',
              gap: titleGap,
              height: '100%',
              maxHeight: `${headerHeight}px`,
              p: 0,
              pt: `${logoTop}px`,
              transition: `max-height ${MOTION.medium}ms ${MOTION.easeOut}`,
              ...REDUCED_MOTION_NO_TRANSITION,
            }}
          >
            <Tooltip title={desktopcollapsedMenu && headerTitle ? headerTitle : ''} placement="right" arrow>
              <Box
                sx={{
                  height: desktopcollapsedMenu ? 38 : 25,
                  transition: `height ${MOTION.medium}ms ${MOTION.easeOut}`,
                  ...REDUCED_MOTION_NO_TRANSITION,
                }}
              >
                <Logo height="100%" type={desktopcollapsedMenu ? 'small' : 'normal'} />
              </Box>
            </Tooltip>

            {headerTitle && !desktopcollapsedMenu && (
              <Transition type="fadeDown" delay={MOTION.short / 1000}>
                <Typography
                  component="p"
                  variant={isCaptionTitle ? 'subtitle2' : 'h2'}
                  color={isCaptionTitle ? COLORS.text.placeholder : COLORS.text.main}
                  sx={{
                    textAlign: 'center',
                    minWidth: '230px',
                    ...(isCaptionTitle && { fontWeight: 500 }),
                  }}
                >
                  {headerTitle}
                </Typography>
              </Transition>
            )}
          </Grid>
          {headerContent && <>{headerContent}</>}

          {headerButtons && (
            <Stack sx={{ gap: 1.5 }}>
              {headerButtons.map((headerButton, ix) => {
                const isCurrent = Boolean(headerButton.title) && segment[0] === headerButton.title;
                return (
                  <Tooltip key={ix} title={desktopcollapsedMenu ? headerButton.text : ''} placement="right" arrow>
                    <Button
                      onClick={headerButton.action}
                      variant={headerButton.variant ?? 'contained'}
                      aria-label={desktopcollapsedMenu ? headerButton.text : undefined}
                      aria-current={isCurrent ? 'page' : undefined}
                      sx={{
                        gap: 0.5,
                        minWidth: 0,
                        whiteSpace: 'nowrap',
                        ...(headerButton.variant === 'outlined' && {
                          borderWidth: 1.5,
                          '&:hover': { borderWidth: 1.5 },
                        }),
                        // Collapsed: icon-only circle instead of a squashed pill
                        ...(desktopcollapsedMenu
                          ? { width: 48, height: 48, minHeight: 48, p: 0, borderRadius: '50%', alignSelf: 'center' }
                          : { width: '100%' }),
                      }}
                    >
                      {headerButton.icon && <MaterialIcon icon={headerButton.icon} />}
                      {/* Unmounted when collapsed: the empty motion div would still take space and push the icon off-center */}
                      {!desktopcollapsedMenu && <Transition type="fadeExpand">{headerButton.text}</Transition>}
                      {headerButton.external && !desktopcollapsedMenu && (
                        <MaterialIcon icon="open_in_new" size={18} aria-hidden="true" />
                      )}
                    </Button>
                  </Tooltip>
                );
              })}
            </Stack>
          )}
          <Divider />
          <Stack component="nav" aria-label="Secciones" sx={{ gap: 0.5, flex: 1 }}>
            {buttonList?.map((button, ix) => {
              const startsSection = Boolean(button.section) && button.section !== buttonList[ix - 1]?.section;
              return (
                // Staggered entrance on the wrapper: an animation on the button itself would override its press scale
                <Box key={ix} sx={{ ...fadeInUpSx(ix), ...(startsSection && ix > 0 && { mt: 1.5 }) }}>
                  {startsSection && !desktopcollapsedMenu && (
                    <Typography component="p" variant="body2" sx={sectionLabelSx}>
                      {button.section}
                    </Typography>
                  )}
                  <Tooltip title={desktopcollapsedMenu ? button.text : ''} placement="right" arrow>
                    <MenuButton
                      component={Link}
                      href={`/${button.title}`}
                      icon={button.icon}
                      text={desktopcollapsedMenu ? undefined : button.text}
                      active={segment[0] === button.title}
                      aria-current={segment[0] === button.title ? 'page' : undefined}
                      sx={{ minWidth: 0, ...button.sx }}
                    />
                  </Tooltip>
                </Box>
              );
            })}
          </Stack>

          {showContactData && (
            <Stack sx={{ gap: 0.25 }}>
              {!desktopcollapsedMenu && (
                <Typography component="p" variant="body2" sx={sectionLabelSx}>
                  ¿Necesitás ayuda?
                </Typography>
              )}
              <Tooltip title={desktopcollapsedMenu ? 'ayuda@directo.com.ar' : ''} placement="right" arrow>
                <MenuButton
                  icon="mail"
                  iconSize={20}
                  text={desktopcollapsedMenu ? '' : 'ayuda@directo.com.ar'}
                  aria-label={desktopcollapsedMenu ? 'ayuda@directo.com.ar' : undefined}
                  href="mailto:ayuda@directo.com.ar"
                  sx={{ minHeight: 36, color: COLORS.text.light, fontFamily: bodyFont, fontSize: '0.8125rem' }}
                />
              </Tooltip>
              <Tooltip title={desktopcollapsedMenu ? '0800 345 6790' : ''} placement="right" arrow>
                <MenuButton
                  icon="call"
                  iconSize={20}
                  text={desktopcollapsedMenu ? '' : '0800 345 6790'}
                  aria-label={desktopcollapsedMenu ? '0800 345 6790' : undefined}
                  href="tel:08003456790"
                  sx={{ minHeight: 36, color: COLORS.text.light, fontFamily: bodyFont, fontSize: '0.8125rem' }}
                />
              </Tooltip>
            </Stack>
          )}

          <Box sx={{ mb: '16px !important' }}>
            {hasAccountCard ? (
              <Stack
                data-testid="menu-account"
                sx={{
                  gap: 1,
                  p: desktopcollapsedMenu ? 1 : 1.5,
                  borderRadius: `${RADIUS.xl}px`,
                  backgroundColor: COLORS.background[0],
                  alignItems: desktopcollapsedMenu ? 'center' : 'stretch',
                }}
              >
                <Stack direction="row" sx={{ gap: 1.5, alignItems: 'center', minWidth: 0 }}>
                  <Tooltip
                    title={
                      desktopcollapsedMenu ? (
                        <List>
                          {accountLines.map((line, ix) => (
                            <ListItem key={ix}>{line}</ListItem>
                          ))}
                        </List>
                      ) : (
                        ''
                      )
                    }
                    placement="right"
                    arrow
                  >
                    <Box
                      aria-hidden="true"
                      sx={{
                        width: 40,
                        height: 40,
                        flex: 'none',
                        borderRadius: '50%',
                        backgroundColor: COLORS.brand.main,
                        color: COLORS.text.inverted,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '0.875rem',
                        fontWeight: 600,
                      }}
                    >
                      {account && !account.icon ? (
                        (account.initials ?? getInitials(account.name))
                      ) : (
                        <MaterialIcon icon={account?.icon ?? 'account_circle'} size={22} />
                      )}
                    </Box>
                  </Tooltip>
                  {!desktopcollapsedMenu && (
                    <Stack sx={{ gap: 0.25, minWidth: 0 }}>
                      {account ? (
                        <>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: COLORS.text.main }}>
                            {account.name}
                          </Typography>
                          {/* One line each: a joined "a · b" wraps and leaves the dot dangling */}
                          {account.details?.map((detail, ix) => (
                            <Typography key={ix} variant="body2" sx={{ fontSize: '0.75rem', color: COLORS.text.light }}>
                              {detail}
                            </Typography>
                          ))}
                        </>
                      ) : (
                        userData?.map((data, ix) => (
                          <Typography key={ix} variant="body2" sx={{ fontSize: '0.75rem', color: COLORS.text.light }}>
                            {data}
                          </Typography>
                        ))
                      )}
                    </Stack>
                  )}
                </Stack>
                {!desktopcollapsedMenu && <Divider sx={{ borderColor: COLORS.border.default }} />}
                {logoutButton}
              </Stack>
            ) : (
              logoutButton
            )}
          </Box>
        </Stack>
      </Stack>
      {setCollapsedMenu && isDesktop && (
        <IconButton
          data-testid="menu-collapse-toggle"
          aria-label={desktopcollapsedMenu ? 'Expandir menú' : 'Contraer menú'}
          aria-expanded={!desktopcollapsedMenu}
          onClick={() => {
            document.getElementsByClassName('MuiTooltip-popper').length === 0 &&
              setCollapsedMenu(!desktopcollapsedMenu);
          }}
          sx={{
            p: 0,
            color: 'inherit',
            outline: `solid 1px ${COLORS.border.brand}`,
            boxShadow: SHADOWS.card,
            left: desktopcollapsedMenu ? 78 : 268,
            top: '50%',
            position: 'absolute',
            margin: 'auto',
            backgroundColor: 'white',
            borderRadius: '50%',
            height: 20,
            width: 20,
            // Invisible hit area: the 20px circle alone is too small to click reliably
            '&::after': { content: '""', position: 'absolute', inset: -12, borderRadius: '50%' },
            '&:hover': { backgroundColor: COLORS.background[0] },
            '&:active': { transform: `scale(${MOTION.pressScale})` },
            // Same timing as the drawer width, so the toggle rides the edge instead of lagging behind it
            transition: `left ${MOTION.medium}ms ${MOTION.easeOut}, background-color ${MOTION.short}ms, transform ${MOTION.fast}ms ${MOTION.easeOut} !important`,
            ...REDUCED_MOTION_NO_TRANSITION,
          }}
        >
          <MaterialIcon
            aria-hidden="true"
            pointer
            icon={'keyboard_arrow_up'}
            size={13}
            sx={{
              transform: desktopcollapsedMenu ? 'rotate(90deg)' : 'rotate(270deg)',
              transition: `transform ${MOTION.medium}ms ${MOTION.easeOut} !important`,
              ...REDUCED_MOTION_NO_TRANSITION,
              textAlign: 'center',
            }}
          />
        </IconButton>
      )}
    </Box>
  );
};
